<template>
  <div class="page">
    <h2 class="page-title">{{ $t('menu.announcements') }}</h2>

    <div class="bento-grid">
      <div class="bento bento--wide glass-card">
        <div v-if="loading" class="muted">{{ $t('common.loading') }}</div>
        <div v-else-if="!current" class="announcement-empty">
          <div class="announcement-empty__icon"><i class="el-icon-circle-check" /></div>
          <div class="announcement-empty__title">{{ $t('announcement.allConfirmed') }}</div>
          <div class="muted announcement-empty__desc">{{ $t('announcement.allConfirmedHint') }}</div>
          <div class="announcement-empty__actions">
            <el-button @click="$router.replace('/dashboard')">{{ $t('menu.dashboard') }}</el-button>
            <el-button type="primary" class="btn-glass" @click="$router.replace(historyPath)">
              {{ $t('announcement.history') }}
            </el-button>
          </div>
        </div>
        <div v-else>
          <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;">
            <div style="font-family:'Poppins',sans-serif;font-weight:600;font-size:16px;">{{ pickLang(current.title) }}</div>
            <div style="display:flex;align-items:center;gap:8px;">
              <span class="tag-pill tag-pill--warn">{{ $t('announcement.required') }}</span>
              <span class="tag-pill">{{ pendingCount }} {{ $t('admin.unconfirmed') }}</span>
            </div>
          </div>
          <div class="content">{{ pickLang(current.content) }}</div>
          <div style="margin-top: 12px;">
            <el-button type="primary" class="btn-glass" :loading="confirming" @click="confirmCurrent">
              {{ $t('common.confirm') }}
            </el-button>
          </div>
        </div>
      </div>

      <div class="bento glass-card">
        <div style="font-family:'Poppins',sans-serif;font-weight:600;margin-bottom:8px;">{{ $t('menu.announcements') }}</div>
        <div class="muted" style="font-size:12px;line-height:1.6;">
          {{ $t('admin.confirmed') }}: {{ confirmedCount }}<br />
          {{ $t('admin.unconfirmed') }}: {{ pendingCount }}
        </div>
        <el-button style="margin-top:10px;" @click="refresh" :loading="loading">{{ $t('common.refresh') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { listAnnouncementsForRole, confirmAnnouncementForRole } from '../api/announcements';

export default {
  name: 'AnnouncementConfirm',
  data() {
    return {
      loading: true,
      confirming: false,
      list: []
    };
  },
  computed: {
    current() {
      return this.list.find((x) => !x.confirmed) || null;
    },
    pendingCount() {
      return (this.list || []).filter((x) => x && !x.confirmed).length;
    },
    confirmedCount() {
      return (this.list || []).filter((x) => x && x.confirmed).length;
    },
    version() {
      return this.$store.state.versions.announcements;
    },
    role() {
      return this.$store.state.user ? this.$store.state.user.role : '';
    },
    historyPath() {
      return this.role === 'SECONDARY_HANDLER' ? '/secondary/announcements' : '/user/announcements';
    }
  },
  created() {
    this.refresh();
  },
  watch: {
    version() {
      this.refresh();
    }
  },
  methods: {
    pickLang(text) {
      if (!text) return '';
      // Convention: use "\n---\n" or "\n\n---\n\n" as zh/en delimiter.
      if (text.indexOf('---') !== -1) {
        const parts = text.split(/\n\s*---\s*\n/);
        if (parts.length >= 2) {
          return this.$i18n.locale === 'zh' ? parts[0] : parts[1];
        }
      }
      // Backward-compat heuristic for the seeded content: zh paragraph + en paragraph.
      if (this.$i18n.locale !== 'zh' && text.indexOf('\n\n') !== -1) {
        const ps = text.split(/\n\n+/);
        if (ps.length >= 2) {
          const first = ps[0];
          const second = ps[ps.length - 1];
          if (/[\u4e00-\u9fff]/.test(first) && !/[\u4e00-\u9fff]/.test(second)) {
            return second;
          }
        }
      }
      return text;
    },
    async refresh() {
      this.loading = true;
      try {
        this.list = await listAnnouncementsForRole(this.role);
      } finally {
        this.loading = false;
      }
    },
    async confirmCurrent() {
      if (!this.current) return;
      this.confirming = true;
      try {
        await confirmAnnouncementForRole(this.role, this.current.id);
        await this.refresh();
        await this.$store.dispatch('refreshMe');
        if (!this.$store.state.mustConfirmAnnouncement) {
          const back = this.$store.state.announcementReturnTo;
          const target = back && back !== '/announcements/confirm' && back !== '/login' ? back : '/dashboard';
          this.$store.commit('clearAnnouncementReturnTo');
          this.$router.replace(target);
        }
      } finally {
        this.confirming = false;
      }
    }
  }
};
</script>

<style scoped>
.content {
  white-space: pre-wrap;
  margin-top: 10px;
  color: rgba(30, 41, 59, 0.95);
  line-height: 1.6;
}
.announcement-empty {
  min-height: 220px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 10px;
  text-align: center;
}
.announcement-empty__icon {
  width: 48px;
  height: 48px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  color: #0f766e;
  background: rgba(20, 184, 166, 0.12);
  border: 1px solid rgba(20, 184, 166, 0.24);
  font-size: 22px;
}
.announcement-empty__title {
  font-weight: 700;
  color: var(--text);
}
.announcement-empty__desc {
  max-width: 360px;
  line-height: 1.6;
}
.announcement-empty__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}
</style>
